import React, { useState, useMemo, useEffect } from "react";
import ExlyModal from "common/Components/ExlyModal";
import MobileCard from "ui/pages/SalesAndMarketing/components/MobileCard";
import { CircularProgress } from "@material-ui/core";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import RefreshIcon from "@material-ui/icons/Refresh";
import { IconButton } from "@material-ui/core";
import GetAppIcon from "@material-ui/icons/GetApp";
import { Pagination } from "common/Components/Pagination";
import { exportToCSV } from "features/Common/utils/common.utils";
import { pluralise } from "utils/Utils";
import {
  getAudienceColumnsDesktop,
  cardContent,
  getAvatarColors,
  getRecipientDisplayName,
} from "./seeMoreStatsModalConfig";
import {
  creatorToolTheme,
  ThemeProvider,
} from "@my-scoot/component-library-legacy";
import { ListContextProvider } from "react-admin";
import { ExlyContextTable } from "common/Components/ExlyTable/ExlyTable";
import { useDesktopMediaQuery } from "features/Common/hooks/useDesktopMediaQuery";
import { getAudienceFilterConfig, U_LETTER } from "./constants";
import {
  useFetchAudience,
  filterAudienceList,
  tableChipsFormatter,
} from "./hooks/SeeMoreStatsAudienceModal.hooks";
import { useRefreshStats } from "utils/hooks/useRefreshStats";
import { DEFAULT_PER_PAGE } from "constants/numbers";

const AudienceMobileComponent = ({ rows, isWhatsApp }) => {
  return (
    <div className="tw-flex tw-flex-col tw-gap-3">
      {rows.map((user, index) => {
        const displayName = getRecipientDisplayName(user, isWhatsApp);
        const colors = getAvatarColors(displayName);
        const avatarChar = displayName
          ? displayName[0].toUpperCase()
          : U_LETTER;

        const headerComponent = (
          <div className="tw-flex tw-items-center tw-gap-2">
            <div
              className="tw-w-8 tw-h-8 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-text-sm tw-font-bold"
              style={{
                backgroundColor: colors.bg,
                color: colors.text,
              }}
            >
              {avatarChar}
            </div>
            <span className="tw-text-[#493AB1] tw-font-medium tw-underline tw-cursor-pointer">
              {displayName}
            </span>
          </div>
        );

        const content = cardContent(user, isWhatsApp);

        return (
          <MobileCard
            key={user.id || index}
            header={headerComponent}
            content={content}
          />
        );
      })}
    </div>
  );
};

const SeeMoreStatsAudienceModal = ({
  open,
  onClose,
  campaignUid,
  field,
  title,
  isShowRefreshStat,
  isWhatsApp = false,
  dripSource,
}) => {
  const isDesktop = useDesktopMediaQuery();
  const [page, setPage] = useState(1);

  useEffect(() => {
    if (open) {
      setPage(1);
    }
  }, [open, campaignUid, field]);

  const { loading, audienceList, totalCount } = useFetchAudience(
    open,
    campaignUid,
    field,
    isWhatsApp,
    dripSource,
    page
  );

  const { handleRefreshStats, loading: refreshLoading } = useRefreshStats();

  const handleDownload = () => {
    exportToCSV(audienceList, `Failed_Recipients_${campaignUid || "List"}.csv`);
  };

  const displayCount =
    totalCount && totalCount > 0 ? totalCount : audienceList.length;

  const countLabel =
    field === "attempted_for"
      ? `${displayCount} ${pluralise("member", displayCount)}`
      : `${displayCount} ${displayCount === 1 ? "person" : "people"}`;

  const modalTitle = `${title} (${countLabel})`;

  const [filterValues, setFilters] = useState({});

  const filteredAudienceList = useMemo(() => {
    return filterAudienceList(audienceList, filterValues);
  }, [audienceList, filterValues]);

  const listContext = {
    data: filteredAudienceList.reduce((acc, item, index) => {
      acc[item.id || index] = item;
      return acc;
    }, {}),
    ids: filteredAudienceList.map((item, index) => item.id || index),
    loaded: !loading,
    loading: loading,
    setFilters: setFilters,
    filterValues: filterValues,
    setSort: () => {},
    total: filteredAudienceList.length,
    currentSort: { field: "id", order: "ASC" },
  };

  return (
    <ExlyModal
      className="!tw-p-0 md:!tw-p-5"
      desktopModalWidth="1000px"
      open={open}
      onClose={onClose}
      modal_props={{
        maxWidth: false,
        PaperProps: {
          style: {
            minWidth: "1000px",
          },
        },
      }}
      customHeader={
        <div className="tw-flex tw-items-center tw-justify-between tw-p-4 tw-border-solid tw-border-b tw-border-r-0 tw-border-l-0 tw-border-t-0 tw-border-[#E7E6E4]">
          <div className="tw-flex tw-items-center tw-gap-2">
            <IconButton size="small" onClick={onClose}>
              <ArrowBackIcon />
            </IconButton>

            <span className="tw-text-lg tw-font-semibold">{modalTitle}</span>
          </div>

          {isShowRefreshStat &&
            (isDesktop ? (
              <button
                className="tw-flex tw-items-center tw-justify-center tw-gap-2 tw-rounded-md tw-border-solid tw-border-[#493AB1] tw-bg-white tw-text-[#493AB1] tw-py-1 tw-px-4 tw-text-[16px] tw-font-medium"
                disabled={refreshLoading}
                onClick={() =>
                  handleRefreshStats({
                    isWhatsApp,
                    campaignUuid: campaignUid,
                  })
                }
              >
                <span>Refresh stats</span>
                {refreshLoading ? (
                  <CircularProgress size={18} color="inherit" />
                ) : (
                  <RefreshIcon style={{ fontSize: 18 }} />
                )}
              </button>
            ) : (
              <IconButton
                size="small"
                disabled={refreshLoading}
                onClick={() =>
                  handleRefreshStats({
                    isWhatsApp,
                    campaignUuid: campaignUid,
                  })
                }
              >
                {refreshLoading ? (
                  <CircularProgress size={18} color="primary" />
                ) : (
                  <RefreshIcon />
                )}
              </IconButton>
            ))}
        </div>
      }
      customFooter={
        totalCount > DEFAULT_PER_PAGE ||
        field === "total_failed" ||
        field === "failed_for" ? (
          <ThemeProvider theme={creatorToolTheme}>
            <div className="tw-w-full tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-3 tw-p-3">
              {totalCount > DEFAULT_PER_PAGE && (
                <div className="tw-flex tw-justify-center tw-items-center tw-w-full">
                  <Pagination
                    page={page}
                    onPageChange={(newPage) => setPage(newPage)}
                    total={totalCount}
                    perPage={DEFAULT_PER_PAGE}
                  />
                </div>
              )}
              {(field === "total_failed" || field === "failed_for") && (
                <button
                  className="tw-w-full tw-flex tw-items-center tw-justify-center tw-gap-2 tw-bg-[#493AB1] tw-text-white tw-rounded-md tw-py-2 tw-font-medium"
                  onClick={handleDownload}
                >
                  <GetAppIcon style={{ fontSize: 18 }} />
                  <p>Get user list with contact details</p>
                </button>
              )}
            </div>
          </ThemeProvider>
        ) : (
          <></>
        )
      }
    >
      {loading ? (
        <div className="tw-flex tw-justify-center tw-items-center tw-py-10">
          <CircularProgress color="primary" />
        </div>
      ) : audienceList.length === 0 ? (
        <div className="tw-text-center tw-py-10 tw-text-gray-500">
          No data available
        </div>
      ) : (
        <div className="tw-py-2">
          <ListContextProvider value={listContext}>
            <ThemeProvider theme={creatorToolTheme}>
              <ExlyContextTable
                useCustomMobileTable
                MobileComponent={(props) => (
                  <AudienceMobileComponent {...props} isWhatsApp={isWhatsApp} />
                )}
                columnConfig={getAudienceColumnsDesktop(isWhatsApp)}
                hidePagination={true}
                tableFilters={
                  isDesktop ? getAudienceFilterConfig(isWhatsApp) : undefined
                }
                tableChipsFormatter={tableChipsFormatter}
                noExportButton={true}
              />
            </ThemeProvider>
          </ListContextProvider>
        </div>
      )}
    </ExlyModal>
  );
};

export default SeeMoreStatsAudienceModal;
